import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, DollarSign, Users, Monitor, Clock, Globe, BarChart3, TrendingUp, Calendar, MapPin, Play, Layers, ChevronRight, Zap, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PLATFORM_COLORS = { twitch: '#9146FF', youtube: '#FF0000', tiktok: '#00F2EA', kick: '#53FC18', instagram: '#E4405F', facebook: '#1877F2', rumble: '#85C742', whatnot: '#FF6B35' };

export default function StreamerProfileView() {
  const { profileId } = useParams();
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [bidCpm, setBidCpm] = useState('');
  const [bidSlots, setBidSlots] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetch = async () => {
      try {
        const [profRes, campRes] = await Promise.all([
          axios.get(`${API}/streamer-profiles/${profileId}`, { headers }),
          axios.get(`${API}/campaigns/my`, { headers }).catch(() => ({ data: [] })),
        ]);
        setProfile(profRes.data);
        setCampaigns(campRes.data);
        // Track profile view
        axios.post(`${API}/streamer-profiles/${profileId}/view`, {}, { headers }).catch(() => {});
      } catch (err) { toast.error('Profile not found'); navigate('/brand'); }
      setLoading(false);
    };
    fetch();
  }, [profileId, API, token]);

  const placeBid = async () => {
    if (!selectedCampaign || !bidCpm || !bidSlots) { toast.error('Fill all fields'); return; }
    try {
      await axios.post(`${API}/placements`, { campaignId: selectedCampaign, streamerId: profile.userId, bidCpm: parseFloat(bidCpm), totalSlots: parseInt(bidSlots) }, { headers });
      toast.success('Order placed!');
      setShowBidDialog(false); setBidCpm(''); setBidSlots(''); setSelectedCampaign('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const fullUrl = (url) => url?.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  const pa = profile?.platformAudience || {};
  const totalReach = profile?.totalReach || Object.values(pa).reduce((s, v) => s + (v || 0), 0) || profile?.avgCCV || 0;
  const activeCampaigns = campaigns.filter(c => c.status === 'active');

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" /></div>;
  if (!profile) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/[0.06] bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-4">
          <Button data-testid="back-to-marketplace" variant="ghost" onClick={() => navigate('/brand')} className="text-gray-500 hover:text-white hover:bg-white/5 h-8 w-8 p-0"><ArrowLeft className="w-4 h-4" /></Button>
          <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Marketplace</span>
          <ChevronRight className="w-3 h-3 text-gray-700" />
          <span className="text-sm text-white font-medium">{profile.displayName || profile.userName}</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* ═══ BANNER + HERO ═══ */}
        {profile.bannerImageUrl && (
          <div className="relative h-48 rounded-xl overflow-hidden border border-white/[0.06] mb-8 -mt-2">
            <img src={fullUrl(profile.bannerImageUrl)} alt="Banner" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          </div>
        )}

        <div className={`flex items-start justify-between mb-10 ${profile.bannerImageUrl ? '-mt-16 relative z-10 px-2' : ''}`}>
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className={`w-20 h-20 rounded-xl bg-[#121212] border-2 ${profile.bannerImageUrl ? 'border-[#050505]' : 'border-white/10'} flex items-center justify-center flex-shrink-0 overflow-hidden shadow-xl`}>
              {profile.profileImageUrl ? (
                <img src={fullUrl(profile.profileImageUrl)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-heading font-black text-gray-600">{(profile.displayName || profile.userName || '?')[0].toUpperCase()}</span>
              )}
            </div>
            {/* Name + meta */}
            <div>
              <h1 className="font-heading text-3xl font-black tracking-tighter text-white">{profile.displayName || profile.userName}</h1>
              <div className="flex items-center gap-3 mt-2">
                {profile.categories?.slice(0, 3).map((c, i) => (
                  <span key={i} className="text-xs text-gray-400 bg-white/5 border border-white/5 px-2 py-0.5 rounded">{c}</span>
                ))}
              </div>
              {profile.bio && <p className="text-sm text-gray-400 mt-3 max-w-lg leading-relaxed">{profile.bio}</p>}
            </div>
          </div>
          {/* CTA */}
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <Button data-testid="profile-place-bid" onClick={() => setShowBidDialog(true)} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold uppercase tracking-widest px-8 py-5 rounded-none text-xs h-auto">
              <DollarSign className="w-4 h-4 mr-2" /> Place Bid
            </Button>
            <span className="text-[10px] font-mono text-gray-600">{activeCampaigns.length} active campaigns</span>
          </div>
        </div>

        {/* ═══ KEY METRICS ═══ */}
        <div className="grid grid-cols-5 gap-3 mb-8">
          {[
            { label: 'Avg CCV', value: (profile.avgCCV || 0).toLocaleString(), icon: Users, color: '#FF3B30' },
            { label: 'Monthly Uniques', value: (profile.uniqueViewersMonthly || 0).toLocaleString(), icon: Eye, color: '#3B82F6' },
            { label: 'Peak Viewers', value: (profile.peakViewers || 0).toLocaleString(), icon: TrendingUp, color: '#10B981' },
            { label: 'Total Reach', value: totalReach.toLocaleString(), icon: Globe, color: '#F59E0B' },
            { label: 'Hrs/Week', value: profile.avgHoursPerWeek || 0, icon: Clock, color: '#7C3AED' },
          ].map((m, i) => (
            <div key={i} className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[9px] font-mono text-gray-600 uppercase tracking-widest">{m.label}</span>
                <m.icon className="w-3.5 h-3.5" style={{ color: m.color }} />
              </div>
              <p className="text-xl font-bold text-white font-mono">{m.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ═══ LEFT COLUMN ═══ */}
          <div className="lg:col-span-2 space-y-6">
            {/* Platform Reach Breakdown */}
            {Object.keys(pa).length > 0 && (
              <div className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-6">
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-4">Platform Reach Distribution</h3>
                <div className="space-y-3">
                  {Object.entries(pa).sort(([,a],[,b]) => b - a).map(([plat, count]) => {
                    const pct = totalReach > 0 ? (count / totalReach * 100) : 0;
                    return (
                      <div key={plat}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[plat.toLowerCase()] || '#666' }} />
                            <span className="text-sm text-white capitalize font-medium">{plat}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-white font-mono font-bold">{count.toLocaleString()}</span>
                            <span className="text-xs text-gray-500 font-mono w-12 text-right">{pct.toFixed(0)}%</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: PLATFORM_COLORS[plat.toLowerCase()] || '#666' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Platforms List */}
            {profile.platforms?.length > 0 && (
              <div className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-6">
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-4">Streaming Platforms</h3>
                <div className="grid grid-cols-2 gap-3">
                  {profile.platforms.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border border-white/5 rounded-lg bg-white/[0.02]">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[(p.name || p).toLowerCase()] || '#666' }} />
                      <div>
                        <p className="text-sm text-white font-medium">{p.name || p}</p>
                        {p.handle && <p className="text-xs text-gray-500 font-mono">@{p.handle}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Demographics */}
            {(profile.demographics?.ageRange || profile.demographics?.topGeos?.length > 0) && (
              <div className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-6">
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-4">Audience Demographics</h3>
                <div className="grid grid-cols-2 gap-4">
                  {profile.demographics?.ageRange && (
                    <div className="p-4 border border-white/5 rounded-lg bg-white/[0.02]">
                      <Users className="w-4 h-4 text-gray-600 mb-2" />
                      <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Age Range</p>
                      <p className="text-lg font-bold text-white font-mono mt-1">{profile.demographics.ageRange}</p>
                    </div>
                  )}
                  {profile.demographics?.topGeos?.length > 0 && (
                    <div className="p-4 border border-white/5 rounded-lg bg-white/[0.02]">
                      <MapPin className="w-4 h-4 text-gray-600 mb-2" />
                      <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Top Regions</p>
                      <p className="text-lg font-bold text-white font-mono mt-1">
                        {Array.isArray(profile.demographics.topGeos) ? profile.demographics.topGeos.join(', ') : profile.demographics.topGeos}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Schedule */}
            {profile.schedule && (
              <div className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-6">
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-3">Stream Schedule</h3>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <p className="text-sm text-white">{profile.schedule}</p>
                  {profile.avgHoursPerWeek > 0 && <Badge className="bg-white/5 text-gray-400 border-white/10 font-mono text-[10px]">{profile.avgHoursPerWeek} hrs/week</Badge>}
                </div>
              </div>
            )}
          </div>

          {/* ═══ RIGHT COLUMN: Rate Card + CTA ═══ */}
          <div className="space-y-6">
            {/* Rate Card */}
            <div className="border border-[#7C3AED]/20 rounded-lg bg-[#7C3AED]/[0.02] p-6">
              <h3 className="text-xs font-mono text-[#7C3AED] uppercase tracking-widest mb-4">Rate Card</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border border-white/5 rounded bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <Play className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm text-gray-300">15s Full-Screen</span>
                  </div>
                  <span className="text-lg font-bold text-white font-mono">${(profile.videoFloorCpm || profile.floorCpm)?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border border-[#7C3AED]/10 rounded bg-[#7C3AED]/[0.02]">
                  <div className="flex items-center gap-2">
                    <Play className="w-3.5 h-3.5 text-[#7C3AED]" />
                    <span className="text-sm text-gray-300">30s Premium</span>
                  </div>
                  <span className="text-lg font-bold text-[#7C3AED] font-mono">${((profile.videoFloorCpm || profile.floorCpm) * 1.8)?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border border-white/5 rounded bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <Layers className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm text-gray-300">Overlay Badge</span>
                  </div>
                  <span className="text-lg font-bold text-white font-mono">${(profile.overlayFloorCpm || 2)?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border border-emerald-500/10 rounded bg-emerald-500/[0.02]">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-3.5 h-3.5 text-emerald-400" />
                    <div><span className="text-sm text-gray-300">Holding Screen</span><br/><span className="text-[9px] text-gray-600">Per stream, exclusive</span></div>
                  </div>
                  <span className="text-lg font-bold text-emerald-400 font-mono">${(profile.holdingScreenRate || 50)?.toFixed(0)}</span>
                </div>
              </div>
              <p className="text-[9px] font-mono text-gray-600 mt-3">CPM (cost per 1,000 impressions) &middot; Bids below floor accepted for review</p>
            </div>

            {/* Quick Stats */}
            <div className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-6">
              <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-4">Estimated Reach</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] font-mono text-gray-600 uppercase">Per Ad Break (avg 3 ads)</p>
                  <p className="text-2xl font-bold text-white font-mono">{(totalReach * 3).toLocaleString()}</p>
                  <p className="text-[10px] text-gray-600 font-mono">impressions across all platforms</p>
                </div>
                <div className="h-px bg-white/5" />
                <div>
                  <p className="text-[10px] font-mono text-gray-600 uppercase">Per Week (est. {profile.avgHoursPerWeek ? Math.round(profile.avgHoursPerWeek / 4) : 5} streams)</p>
                  <p className="text-2xl font-bold text-white font-mono">{(totalReach * 3 * (profile.avgHoursPerWeek ? Math.round(profile.avgHoursPerWeek / 4) : 5)).toLocaleString()}</p>
                  <p className="text-[10px] text-gray-600 font-mono">estimated weekly impressions</p>
                </div>
                <div className="h-px bg-white/5" />
                <div>
                  <p className="text-[10px] font-mono text-gray-600 uppercase">Monthly Reach</p>
                  <p className="text-2xl font-bold text-emerald-400 font-mono">{(totalReach * 3 * (profile.avgHoursPerWeek ? Math.round(profile.avgHoursPerWeek / 4) : 5) * 4).toLocaleString()}</p>
                  <p className="text-[10px] text-gray-600 font-mono">estimated monthly impressions</p>
                </div>
              </div>
            </div>

            {/* Place Bid CTA */}
            <Button data-testid="profile-place-bid-bottom" onClick={() => setShowBidDialog(true)}
              className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold uppercase tracking-widest py-5 rounded-none text-sm h-auto">
              <DollarSign className="w-4 h-4 mr-2" /> Place Bid on {profile.displayName || profile.userName}
            </Button>

            {/* Content Categories */}
            {profile.categories?.length > 0 && (
              <div className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-6">
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-3">Content</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.categories.map((c, i) => (
                    <span key={i} className="text-xs text-gray-300 bg-white/5 border border-white/5 px-2.5 py-1 rounded">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Bid Dialog */}
      <Dialog open={showBidDialog} onOpenChange={setShowBidDialog}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Bid on {profile.displayName}</DialogTitle>
            <DialogDescription className="text-gray-500">Video CPM: ${(profile.videoFloorCpm || profile.floorCpm)?.toFixed(2)} (15s) / ${((profile.videoFloorCpm || profile.floorCpm) * 1.8)?.toFixed(2)} (30s) &middot; Overlay CPM: ${(profile.overlayFloorCpm || 2)?.toFixed(2)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-mono text-gray-500 uppercase">Campaign</Label>
              <Select value={selectedCampaign} onValueChange={setSelectedCampaign}><SelectTrigger className="bg-black/40 border-white/10 text-white mt-1"><SelectValue placeholder="Choose campaign" /></SelectTrigger><SelectContent className="bg-[#121212] border-white/10">{activeCampaigns.map(c => <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs font-mono text-gray-500 uppercase">CPM Bid ($)</Label><Input data-testid="profile-bid-cpm" type="number" step="0.01" value={bidCpm} onChange={e => setBidCpm(e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs font-mono text-gray-500 uppercase">Number of Spots</Label><Input data-testid="profile-bid-slots" type="number" value={bidSlots} onChange={e => setBidSlots(e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" placeholder="25" /></div>
            {bidCpm && bidSlots && totalReach > 0 && (() => {
              const slotP = (totalReach * parseFloat(bidCpm || 0)) / 1000;
              const total = slotP * parseInt(bidSlots || 0);
              return (
                <div className="p-3 border border-[#7C3AED]/20 rounded bg-[#7C3AED]/[0.02] text-xs font-mono">
                  <div className="flex justify-between"><span className="text-gray-500">Slot Price</span><span className="text-white">${slotP.toFixed(2)}</span></div>
                  <div className="flex justify-between mt-1"><span className="text-gray-500">Spots</span><span className="text-white">x {bidSlots}</span></div>
                  <div className="flex justify-between mt-1 pt-1 border-t border-white/5"><span className="text-gray-400 font-bold">Total</span><span className="text-white font-bold">${total.toFixed(2)}</span></div>
                </div>
              );
            })()}
            {bidCpm && profile.floorCpm && parseFloat(bidCpm) < profile.floorCpm && (
              <div className="p-2.5 border border-yellow-500/20 bg-yellow-500/5 rounded text-xs text-yellow-400">Below floor — bid will be sent to creator for review.</div>
            )}
          </div>
          <DialogFooter><Button onClick={placeBid} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold w-full" disabled={!bidCpm || !selectedCampaign || !bidSlots}><DollarSign className="w-4 h-4 mr-1" />Place Order</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
